import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { PremiumLock } from "@/components/PremiumLock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { InfoTip } from "@/components/InfoTip";
import { CalendarClock, Check, Loader2, ArrowRight, Info, Flag } from "lucide-react";

const TASK_LINKS = {
  deep_clean: { type: "scroll", target: "moveout-checklist" },
  prep_plan: { type: "scroll", target: "prep-plan-card" },
  inspection: { type: "scroll", target: "inspection-request-card" },
  forwarding: { type: "scroll", target: "forwarding-notice-card" },
  deposit_letter: { type: "route", target: (pid) => `/properties/${pid}/dispute-letter` },
};
const daysUntil = (d) => {
  try {
    const end = new Date(d + "T00:00:00"); const now = new Date(); now.setHours(0, 0, 0, 0);
    return Math.round((end - now) / 86400000);
  } catch { return null; }
};

export function MoveOutCountdown({ propertyId, archived = false }) {
  const { isPremium } = useTier();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateInput, setDateInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!isPremium) { setLoading(false); return; }
    try {
      const { data } = await api.get(`/properties/${propertyId}/moveout-plan`);
      setData(data); setDateInput(data.lease_end_date || "");
    } catch { /* interceptor handles 402 */ }
    finally { setLoading(false); }
  }, [propertyId, isPremium]);

  useEffect(() => { load(); }, [load]);

  const saveDate = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${propertyId}/lease-end`, { lease_end_date: dateInput || null });
      toast.success("Lease-end date saved"); load();
    } catch { toast.error("Could not save date"); }
    finally { setSaving(false); }
  };

  const toggle = async (key, done) => {
    try {
      const { data } = await api.put(`/properties/${propertyId}/moveout-plan`, { key, done });
      setData((d) => ({ ...d, plan: data.plan }));
    } catch { toast.error("Could not update task"); }
  };

  const runLink = (key) => {
    const l = TASK_LINKS[key];
    if (!l) return;
    if (l.type === "route") navigate(l.target(propertyId));
    else document.querySelector(`[data-testid="${l.target}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!isPremium) return (
    <PremiumLock title="Move-Out Countdown Plan" feature="moveout_plan" icon={CalendarClock} testId="moveout-plan-locked" tip="moveOutCountdown"
      description="A guided runway from your lease-end date to full move-out documentation." />
  );
  if (loading || !data) return null;

  const leaseEnd = data.lease_end_date;
  const dleft = leaseEnd ? daysUntil(leaseEnd) : null;
  const tasks = data.plan?.tasks || [];
  const doneCount = tasks.filter((t) => t.done).length;
  const pct = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0;
  const showBanner = !archived && dleft != null && dleft <= 60 && dleft >= 0;

  return (
    <div data-testid="moveout-countdown" className="rounded-2xl border border-amber-border bg-amber-soft p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <CalendarClock size={20} className="text-amber-ink" />
        <h2 className="font-heading text-lg font-bold text-navy">Move-Out Countdown Plan</h2>
        <InfoTip topic="moveOutCountdown" />
      </div>

      {showBanner && (
        <div data-testid="moveout-countdown-banner" className="mt-3 rounded-xl bg-navy text-white px-4 py-3 flex items-center gap-2">
          <Flag size={16} className="text-amber-ink" />
          <span className="font-semibold">Move-out countdown: {dleft} {dleft === 1 ? "day" : "days"}</span>
        </div>
      )}

      <div className="mt-4">
        <Label className="text-sm font-medium text-navy">Lease-end date</Label>
        <div className="mt-1.5 flex gap-2">
          <Input data-testid="moveout-leaseend-input" type="date" value={dateInput}
            onChange={(e) => setDateInput(e.target.value)} className="h-11 rounded-xl max-w-xs" />
          <Button data-testid="moveout-leaseend-save" onClick={saveDate} disabled={saving}
            className="h-11 rounded-xl bg-navy hover:bg-navy-hover text-white">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save"}
          </Button>
        </div>
        {leaseEnd && dleft != null && !showBanner && (
          <p className="mt-1.5 text-xs text-[#64748B]">{dleft < 0 ? `Lease ended ${Math.abs(dleft)} days ago` : `${dleft} days until lease end`}.</p>
        )}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-navy">Move-out checklist</span>
          <span className="text-[#64748B]" data-testid="moveout-progress">{doneCount}/{tasks.length} done</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-white/70 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-3 space-y-2">
          {tasks.map((t) => (
            <div key={t.key} data-testid={`moveout-task-${t.key}`}
              className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3">
              <button data-testid={`moveout-toggle-${t.key}`} onClick={() => toggle(t.key, !t.done)}
                className={`grid place-items-center h-6 w-6 rounded-md border shrink-0 ${t.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-[#CBD5E1] text-transparent"}`}>
                <Check size={14} />
              </button>
              <span className={`text-sm flex-1 ${t.done ? "line-through text-[#94A3B8]" : "text-navy"}`}>{t.label}</span>
              {TASK_LINKS[t.key] && (
                <button data-testid={`moveout-open-${t.key}`} onClick={() => runLink(t.key)}
                  className="text-xs font-semibold text-brand hover:underline inline-flex items-center gap-1 shrink-0">
                  Open <ArrowRight size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-[#475569]">
        <Info size={14} className="mt-0.5 shrink-0 text-amber-ink" />
        <span>Dates are yours — we don't calculate legal deadlines.</span>
      </div>
    </div>
  );
}
